import { MenuItem, Paper } from '@material-ui/core';
import MoreHoriz from '@material-ui/icons/MoreHoriz';
import produce from 'immer';
import { divIcon, DivIcon, LeafletEvent, LeafletMouseEvent } from 'leaflet';
import {
	createRef,
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useState
} from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { Marker } from 'react-leaflet';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
	ConfirmationDialog,
	ContextMenuButton,
	IConfirmationDialogProps,
	LeafletPopup
} from '..';
import { usePrevious } from '../../../hooks';
import {
	AppState,
	Attachment,
	attachmentEditorHighlightAttachment,
	attachmentEditorSelectAttachment,
	ILatLng,
	pushMessage
} from '../../../redux';
import {
	compareCoordinates,
	convertLatLngInterfaceToLatLng
} from '../../../utils';
import { AttachmentIcon, AttachmentPinIcon, SubTitle1 } from '../../atoms';

import './attachment-pin.styles.scss';

export interface IAttachmentPinOwnProps {
	attachment: Attachment;
	removePin: (attachment: Attachment) => void;
}

export interface IAttachmentPinStateProps {
	currentAttachment?: Attachment;
	highlightedAttachment?: Attachment;
	editAttachment: (attachment: Attachment) => void;
	highlightAttachment: (attachment: Attachment) => void;
}

function AttachmentPinView(
	props: IAttachmentPinOwnProps & IAttachmentPinStateProps
): ReactElement {
	const { attachment, currentAttachment, highlightedAttachment, highlightAttachment, removePin, editAttachment } = props;
	const { t } = useTranslation('map');
	const [marker, setMarker] = useState<L.Marker>();
	const [dialogProps, setDialogProps] = useState<IConfirmationDialogProps>({
		open: false,
		title: t('pin.deletePin'),
		confirmText: t('pin.deletePin'),
		bodyText: t('pin.confirmDeletePin'),
		onConfirm: onConfirmDelete,
		onCancel: closeDialog
	});
	const markerRef: RefObject<L.Marker> = createRef<L.Marker>();
	const [hasMoved, setHasMoved] = useState<boolean>(false);

	useEffect(() => {
		if (markerRef.current && !marker) {
			setMarker(markerRef.current);
			markerRef.current.on('move', onMove);
			markerRef.current.on('click', () => highlightAttachment(attachment));
		}
	}, [markerRef]);

	const isSelected: boolean = useMemo<boolean>(() => {
		return (
			currentAttachment !== undefined &&
			currentAttachment.order === attachment.order
		);
	}, [currentAttachment]);

	const previousIsSelected: boolean | undefined =
		usePrevious<boolean>(isSelected);

	useEffect(() => {
		if (!marker) return;
		marker.off('move', onMove);
		marker.on('move', onMove);
	}, [isSelected]);

	useEffect(() => {
		if (previousIsSelected && !isSelected) {
			setHasMoved(false);
		}
	}, [isSelected]);

	const markerIcon: DivIcon = useMemo<DivIcon>(() => {
		return divIcon({
			className: `attachment-icon ${highlightedAttachment?.order === attachment.order ? 'selected' : ''}`,
			html: renderToString(<AttachmentPinIcon />)
		});
	}, [isSelected, highlightedAttachment?.order, attachment.order]);

	const markerPosition: ILatLng = useMemo<ILatLng>(() => {
		if (isSelected) return currentAttachment!.pointOnMap as ILatLng;
		return attachment.pointOnMap as ILatLng;
	}, [currentAttachment?.pointOnMap, attachment.pointOnMap]);

	function getCircleMarkerIcon(): DivIcon {
		return divIcon({
			className: 'attachment-icon-circle',
			html: '<div class="circle-pulse"></div>'
		});
	}

	function onMove(event: LeafletEvent): void {
		if (!isSelected || !isDragEvent(event)) return;
		if (!currentAttachment || !currentAttachment.pointOnMap) return;

		const location: ILatLng = {
			lat: (event as LeafletMouseEvent).latlng.lat,
			lng: (event as LeafletMouseEvent).latlng.lng
		};

		if (compareCoordinates(location, currentAttachment.pointOnMap)) return;

		setHasMoved(true);
		const newAttachment: Attachment = produce(
			currentAttachment,
			(draft: Attachment) => {
				draft.pointOnMap = {
					lat: location.lat,
					lng: location.lng
				};
			}
		);
		editAttachment(newAttachment);
	}

	function isDragEvent(event: LeafletEvent): boolean {
		return 'originalEvent' in event;
	}

	function onConfirmDelete(): void {
		removePin(attachment);
		closeDialog();
		pushMessage(t('pin.removedPin'));
	}

	function closeDialog(): void {
		setDialogProps({ ...dialogProps, open: false });
	}

	return (
		<>
			{isSelected && !hasMoved && (
				<Marker
					position={markerPosition}
					icon={getCircleMarkerIcon()}
				/>
			)}
			<Marker
				position={convertLatLngInterfaceToLatLng(markerPosition)}
				icon={markerIcon}
				draggable={isSelected}
				ref={markerRef}
			>
				{marker && (
					<LeafletPopup
						open={currentAttachment === undefined}
						marker={marker}
						className="attachment-popup"
					>
						<Paper square className="attachment-popup-container" onClick={() => highlightAttachment(attachment)}>
							<SubTitle1>{attachment.order}</SubTitle1>
							<AttachmentIcon
								type={attachment.type}
								color="#1F53FA"
								noBackground
								noPadding
							/>
							{!attachment.isAuthorContent && (
								<ContextMenuButton
									size="small"
									className="context-menu-button"
									closeOnSelect
									icon={<MoreHoriz />}
								>
									<MenuItem
										onClick={() =>
											editAttachment(attachment)
										}
									>
										{t('pin.movePin')}
									</MenuItem>
									<MenuItem
										onClick={() =>
											setDialogProps({
												...dialogProps,
												open: true
											})
										}
									>
										{t('pin.deletePin')}
									</MenuItem>
								</ContextMenuButton>
							)}
						</Paper>
					</LeafletPopup>
				)}
				<ConfirmationDialog {...dialogProps} />
			</Marker>
		</>
	);
}

function mapStateToProps(state: AppState) {
	return {
		currentAttachment: state.attachmentsEditor.currentAttachment,
		highlightedAttachment: state.attachmentsEditor.highlightAttachment,
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		editAttachment: (attachment: Attachment) =>
			dispatch(attachmentEditorSelectAttachment(attachment)),
		highlightAttachment: (attachment: Attachment) =>
			dispatch(attachmentEditorHighlightAttachment(attachment)),
	};
}

export const AttachmentPin = connect(
	mapStateToProps,
	mapDispatchToProps
)(AttachmentPinView);
